import { RadioGroup } from "@radix-ui/react-radio-group";
import React, { useState, useEffect } from "react";

import { updateMessage } from "@/api/message/message.api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { RadioButton } from "@/components/ui/radioButton";
import { UseSnackbar } from "@/components/snackbar/snackbarContext";
import { logEventRecord } from "@/utils/analytics";

const feedbackReasons = [
  { id: "yes_as_is", label: "Yes, as is" },
  { id: "yes_with_minor_edits", label: "Yes, with minor edits" },
  { id: "yes_with_major_edits", label: "Yes, with major edits" },
  { id: "no_unusable", label: "No, unusable" },
];

interface FeedbackPopupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  responseId: string;
  isFeedbackButtonDisabled: boolean;
  variant?: string;
  cssClass?: string;
  gaFeedbackType?: string;
}

export const FeedbackPopup: React.FC<FeedbackPopupProps> = ({
  open,
  onOpenChange,
  responseId,
  isFeedbackButtonDisabled = false,
  variant = "outline",
  cssClass = "",
  gaFeedbackType,
}) => {
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [additionalFeedback, setAdditionalFeedback] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const showSnackbar: any = UseSnackbar();
  const isSubmitDisabled = !selectedReason || additionalFeedback.trim() === "";

  const handleReasonSelect = (id: string) => {
    setSelectedReason(id);
  };

  const handleSubmit = async () => {
    if (isSubmitDisabled) return;

    setIsSubmitting(true);
    try {
      const payload = {
        message_id: responseId || "",
        feedback_reasons: [selectedReason],
        additional_feedback: additionalFeedback,
      };
      await updateMessage(payload);

      onOpenChange(false);
      setSelectedReason(null);
      setAdditionalFeedback("");

      if (gaFeedbackType === "copilot") {
        logEventRecord("copilot_feedback_submit", {
          event_category: "copilot_feedback",
          event_action: "submit",
          event_label: "copilot_feedback_submit",
        });
      }
      if (gaFeedbackType === "generation") {
        logEventRecord("generation_feedback_submit", {
          event_category: "generation_feedback",
          event_action: "submit",
          event_label: "generation_feedback_submit",
        });
      }
    } catch (error) {
      showSnackbar({
        message: "Error submitting feedback. Please try again.",
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (open) {
      setSelectedReason(null);
      setAdditionalFeedback("");
    }
  }, [open, responseId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button
          sx={{
            textTransform: "capitalize",
            fontSize: "0.25rem",
          }}
          variant={variant}
          className={`text-xs p-2 ml-2 ${cssClass}`}
          disabled={isFeedbackButtonDisabled}
        >
          Provide Feedback
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl">
            Was the output helpful?
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <RadioGroup
              className="flex flex-col space-y-2"
              onValueChange={handleReasonSelect}
            >
              {feedbackReasons.map((reason) => (
                <div key={reason.id} className="flex items-start space-x-2">
                  <RadioButton
                    value={reason.id}
                    id={reason.id}
                    className="peer"
                  />
                  <label
                    htmlFor={reason.id}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    {reason.label}
                  </label>
                </div>
              ))}
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Textarea
              placeholder="Please explain further based on your selection above."
              value={additionalFeedback}
              onChange={(e) => setAdditionalFeedback(e.target.value)}
              className="min-h-[6.25rem]"
              required
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            className="rounded-full"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || isSubmitDisabled}
            className="bg-brand-secondary hover:bg-brand-tertiary rounded-full"
          >
            {isSubmitting ? "Submitting..." : "Submit Feedback"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackPopup;
